/**
 * A past sheet's Typst version in the 2026 archive (Phase 8, Lane S), as the past-sheet
 * lists, the week views and the new-sheet form say it: a chip, and a sentence of what a
 * course sheet started from it gets.
 *
 * Also `useCourseAvailable`: whether this server can build course (2026 Typst) sheets —
 * which is when a new sheet is one by default, and so when the Typst version is the one
 * that counts.
 */
import { useEffect, useState } from 'react';
import clsx from 'clsx';
import type { ArchiveSheetSummary } from '@studio/shared';
import { api } from '../api';
import { tx, txn } from '../i18n';

/** "Typst version available", with what it holds on hover. */
export function TypstSheetChip({
  typst,
  className,
  short = false,
}: {
  typst: ArchiveSheetSummary | null | undefined;
  className?: string;
  /** just "Typst", for a narrow list */
  short?: boolean;
}) {
  if (!typst) return null;
  return (
    <span
      className={clsx('chip chip-teal shrink-0', short && 'px-1.5 text-[10px]', className)}
      title={typstSheetText(typst)}
      data-testid="typst-sheet"
    >
      {short ? 'Typst' : tx('Typst version available')}
    </span>
  );
}

/** What the Typst version holds, in a sentence. */
export function typstSheetText(typst: ArchiveSheetSummary): string {
  const parts = [txn(typst.exercises, '{n} exercise', '{n} exercises')];
  if (typst.headings) parts.push(txn(typst.headings, '{n} section heading', '{n} section headings'));
  // all of them is a sheet only the archive has: there is no 2025 sheet to compare with
  const extra =
    typst.extra && typst.extra < typst.exercises ? ` ${tx('({n} not on the 2025 sheet in Studio)', { n: typst.extra })}` : '';
  return tx('Typst version in the 2026 archive: {parts}{extra}. A course-template sheet started from it takes this version, natively in Typst.', {
    parts: parts.join(', '),
    extra,
  });
}

let courseAvailable: Promise<boolean> | null = null;

/** Whether the server has the 2026 course template (asked once per page load). */
export function useCourseAvailable(): boolean {
  const [available, setAvailable] = useState(false);
  useEffect(() => {
    let live = true;
    courseAvailable ??= api
      .courseStatus()
      .then((s) => Boolean(s.available))
      // an older server has no such route: no course template
      .catch(() => false);
    void courseAvailable.then((v) => live && setAvailable(v));
    return () => {
      live = false;
    };
  }, []);
  return available;
}
